/**
 * 403 (#1317): Freshchat's web messenger. The account loader comes from the runtime `config.js` (Constitution II) and
 * is added once; Freshchat draws its own chat button. The logged-in customer is handed over with Freshchat's own
 * JavaScript API. 🔴 `user.clear()` RESETS the customer's conversation, so it runs only when the identity changes
 * (another customer, a logout, or a logged-out page load while a customer is remembered) — never on a re-render.
 */
interface FreshchatApi {
  open(payload?: unknown): void;
  isInitialized?(): boolean;
  on(event: string, callback: () => void): void;
  setExternalId?(id: string): void;
  user: {
    setProperties(props: Record<string, string>): unknown;
    clear(): unknown;
  };
}

declare global {
  interface Window {
    fcWidget?: FreshchatApi;
  }
}

export const FRESHCHAT_SCRIPT_ID = 'carii-freshchat';
// Who Freshchat was last told about on this device, so a logged-out reload can clear it (Freshchat keeps its user).
const REMEMBERED = 'carii.chat.user';
const READY_TIMEOUT_MS = 10_000;
const POLL_MS = 100;

let requested = false;
let current: string | null | undefined; // undefined: nothing applied since this page loaded

const remembered = (): string | null => {
  try {
    return localStorage.getItem(REMEMBERED);
  } catch {
    return null;
  }
};
const remember = (id: string | null) => {
  try {
    if (id) localStorage.setItem(REMEMBERED, id);
    else localStorage.removeItem(REMEMBERED);
  } catch {}
};

/** Tests only: forget the loader and the customer, as a fresh page would (`keepStorage` keeps the device memory). */
export function resetFreshchatForTests(opts: { keepStorage?: boolean } = {}): void {
  requested = false;
  current = undefined;
  if (!opts.keepStorage) remember(null);
}

/**
 * Add the loader once. `onReady` runs once Freshchat's widget API exists and is initialized — whether it was already,
 * or announces `widget:loaded` later (the loader defines `window.fcWidget` itself, so it is waited for). `onFailed`
 * runs on a script error or when nothing is ready after 10 s. Exactly one of the two runs.
 */
export function loadFreshchat(
  scriptUrl: string,
  onReady: () => void,
  onFailed: () => void,
  doc: Document = document,
): void {
  if (requested || doc.getElementById(FRESHCHAT_SCRIPT_ID)) return;
  requested = true;
  let settled = false;
  const settle = (ok: boolean) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    clearInterval(poll);
    (ok ? onReady : onFailed)();
  };
  let poll: ReturnType<typeof setInterval> | undefined;
  const timer = setTimeout(() => settle(false), READY_TIMEOUT_MS);
  const s = doc.createElement('script');
  s.id = FRESHCHAT_SCRIPT_ID;
  s.src = scriptUrl;
  s.async = true;
  s.setAttribute('chat', 'true');
  s.onerror = () => settle(false);
  s.onload = () => {
    let listening = false;
    const check = () => {
      const api = (doc.defaultView ?? window).fcWidget;
      if (!api) return;
      if (api.isInitialized?.()) return settle(true);
      if (!listening) {
        listening = true;
        api.on('widget:loaded', () => settle(true));
      }
    };
    check();
    if (!settled) poll = setInterval(check, POLL_MS);
  };
  doc.body.appendChild(s);
}

/** Hand Freshchat the customer, or nobody (logged out, or staff impersonating). */
export function applyFreshchatCustomer(
  customer: {
    _id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  } | null,
  api: FreshchatApi | undefined = typeof window === 'undefined' ? undefined : window.fcWidget,
): void {
  if (!api) return;
  const next = customer ? String(customer._id) : null;
  const before = current === undefined ? remembered() : current;
  current = next;
  remember(next);
  // Still nobody: nothing to tell and nothing to clear.
  if (before === next && !customer) return;
  const cleared = before && before !== next ? Promise.resolve(api.user.clear()) : Promise.resolve();
  void cleared
    .catch(() => {})
    .then(() => {
      if (!customer || current !== next) return;
      api.setExternalId?.(next as string);
      void Promise.resolve(
        api.user.setProperties({
          firstName: customer.first_name ?? '',
          lastName: customer.last_name ?? '',
          email: customer.email,
          phone: customer.phone ?? '',
        }),
      ).catch(() => {});
    });
}

export function openFreshchat(
  api: FreshchatApi | undefined = typeof window === 'undefined' ? undefined : window.fcWidget,
): void {
  api?.open();
}
